import React, { useState, useEffect } from 'react';
import { subscribeToCollection, saveDocument, deleteDocument } from '../firebase';
import AssignmentBox from './AssignmentBox';
import LoadingChip from './LoadingChip';

/**
 * Generic, reusable "assignment list" section — one consistent UI for
 * viewing, adding, and editing a collection of items (rooms, cars, etc.)
 * each of which has some metadata fields plus a list of assigned people.
 *
 * Used by Stay.js to render both Room Assignments and Car Assignments with
 * matching interaction patterns (per-card [Edit], inline add form, delete
 * in edit mode) instead of each having its own bespoke UI.
 */
export default function AssignmentSection({
  icon: Icon,
  title,
  collectionName,
  idPrefix,
  users = [],
  sortFn,
  metaFields = [], // [{ key, label, placeholder, type: 'text'|'number', flex, width, badge, badgePrefix }]
  assignmentField, // e.g. 'occupant' or 'drivers'
  assignmentMode = 'array', // 'array' | 'csv'
  assignmentLabel = 'Assigned',
  addAssignmentPlaceholder = '-- Add --',
  emptyAssignmentPlaceholder = 'None assigned',
  addButtonLabel = '+ Add',
  nameLabel = 'Name',
  namePlaceholder = '',
  loadingText = 'Loading...',
  emptyStateText = 'Nothing here yet.',
  gridMinWidth = '220px',
}) {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Add form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newMeta, setNewMeta] = useState({});
  const [newAssignment, setNewAssignment] = useState([]);

  // Per-card edit state
  const [editingId, setEditingId] = useState(null);
  const [editMeta, setEditMeta] = useState({});
  const [editAssignment, setEditAssignment] = useState([]);

  useEffect(() => {
    const unsubscribe = subscribeToCollection(collectionName, (data) => {
      const sorted = sortFn ? [...data].sort(sortFn) : [...data];
      setItems(sorted);
      setIsLoading(false);
    });
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionName]);

  const getAssignmentArray = (item) => {
    const raw = item[assignmentField];
    if (assignmentMode === 'array') return Array.isArray(raw) ? raw : [];
    return raw ? String(raw).split(/[,/&]/).map(n => n.trim()).filter(Boolean) : [];
  };

  const toStoredAssignment = (arr) => assignmentMode === 'array' ? arr : arr.join(', ');

  const buildMetaPatch = (source) => {
    const patch = {};
    metaFields.forEach(f => {
      const val = source[f.key] || '';
      patch[f.key] = f.type === 'number' ? (val ? parseInt(val, 10) : null) : val.trim();
    });
    return patch;
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    const id = `${idPrefix}_${Date.now()}`;
    const doc = {
      id,
      name: newName.trim(),
      [assignmentField]: toStoredAssignment(newAssignment),
      ...buildMetaPatch(newMeta),
    };
    await saveDocument(collectionName, id, doc);
    setNewName('');
    setNewMeta({});
    setNewAssignment([]);
    setShowAddForm(false);
  };

  const handleStartEdit = (item) => {
    setEditingId(item.id);
    const meta = {};
    metaFields.forEach(f => { meta[f.key] = item[f.key] != null ? String(item[f.key]) : ''; });
    setEditMeta(meta);
    setEditAssignment(getAssignmentArray(item));
  };

  const handleSaveEdit = async (item) => {
    const doc = {
      ...item,
      [assignmentField]: toStoredAssignment(editAssignment),
      ...buildMetaPatch(editMeta),
    };
    await saveDocument(collectionName, item.id, doc);
    setEditingId(null);
  };

  const handleCancelEdit = () => setEditingId(null);

  const handleDeleteItem = async (item) => {
    if (!window.confirm(`Delete "${item.name}"? This cannot be undone.`)) return;
    await deleteDocument(collectionName, item.id);
    if (editingId === item.id) setEditingId(null);
  };

  return (
    <div className="assignment-section" style={{ marginBottom: '32px' }}>
      <div className="assignment-section-header" style={{ marginBottom: '16px' }}>
        <div className="view-title">
          <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px', fontFamily: 'var(--font-title)', fontSize: '1.4rem', margin: 0 }}>
            {Icon && <Icon size={22} strokeWidth={1.75} />}
            {title}
          </h3>
        </div>
        <button
          className={showAddForm ? 'btn-secondary' : 'btn-primary'}
          style={{ fontSize: '0.85rem', padding: '8px 18px', borderRadius: '0px' }}
          onClick={() => { setShowAddForm(v => !v); setEditingId(null); }}
        >
          {showAddForm ? '✕ Cancel' : addButtonLabel}
        </button>
      </div>

      {showAddForm && (
        <form
          onSubmit={handleAddItem}
          className="glass-panel"
          style={{ padding: '20px', marginBottom: '20px', display: 'flex', flexDirection: 'column', gap: '14px', borderLeft: '3px solid var(--color-primary)' }}
        >
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <div style={{ flex: '2', minWidth: '160px' }}>
              <label style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', display: 'block', marginBottom: '4px' }}>
                {nameLabel} <span style={{ color: 'var(--color-danger)' }}>*</span>
              </label>
              <input
                type="text"
                className="input-field"
                placeholder={namePlaceholder}
                value={newName}
                onChange={e => setNewName(e.target.value)}
                style={{ width: '100%', fontSize: '0.85rem' }}
              />
            </div>
            {metaFields.map(f => (
              <div key={f.key} style={{ flex: f.flex || '1', minWidth: f.width || '140px' }}>
                <label style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', display: 'block', marginBottom: '4px' }}>{f.label}</label>
                <input
                  type={f.type || 'text'}
                  className="input-field"
                  placeholder={f.placeholder || ''}
                  value={newMeta[f.key] || ''}
                  onChange={e => setNewMeta(prev => ({ ...prev, [f.key]: e.target.value }))}
                  style={{ width: '100%', fontSize: '0.85rem' }}
                />
              </div>
            ))}
          </div>

          <AssignmentBox
            label={`${assignmentLabel} (optional)`}
            selectedIds={newAssignment}
            users={users}
            isEditing={true}
            onAdd={(id) => setNewAssignment(prev => [...prev, id])}
            onRemove={(id) => setNewAssignment(prev => prev.filter(a => a !== id))}
            addPlaceholder={addAssignmentPlaceholder}
            emptyPlaceholder={emptyAssignmentPlaceholder}
          />

          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="submit" className="btn-primary" style={{ fontSize: '0.8rem', padding: '7px 20px' }}>Save</button>
            <button type="button" className="btn-secondary" style={{ fontSize: '0.8rem', padding: '7px 14px' }} onClick={() => setShowAddForm(false)}>Cancel</button>
          </div>
        </form>
      )}

      <div className="room-grid" style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${gridMinWidth}, 1fr))`, gap: '12px' }}>
        {isLoading ? (
          <LoadingChip text={loadingText} containerStyle={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'center', padding: '40px 0' }} />
        ) : items.length === 0 ? (
          <div className="glass-panel" style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-muted)', gridColumn: '1 / -1' }}>
            {emptyStateText}
          </div>
        ) : (
          items.map(item => {
            const isEditingItem = editingId === item.id;
            const assignmentArr = getAssignmentArray(item);

            if (isEditingItem) {
              return (
                <div key={item.id} className="room-card" style={{ padding: '14px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ fontSize: '0.95rem', fontWeight: '700', margin: 0, color: 'var(--color-text-main)' }}>{item.name}</h3>
                    <button
                      className="btn-inline-delete-cross"
                      style={{ lineHeight: 1, fontSize: '1rem' }}
                      title={`Delete ${item.name}`}
                      onClick={() => handleDeleteItem(item)}
                    >×</button>
                  </div>

                  {metaFields.length > 0 && (
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {metaFields.map(f => (
                        <div key={f.key} style={{ flex: f.flex || '1', minWidth: f.width || '120px' }}>
                          <label style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', display: 'block', marginBottom: '3px' }}>{f.label}</label>
                          <input
                            type={f.type || 'text'}
                            className="input-field"
                            value={editMeta[f.key] || ''}
                            onChange={e => setEditMeta(prev => ({ ...prev, [f.key]: e.target.value }))}
                            placeholder={f.placeholder || ''}
                            style={{ width: '100%', fontSize: '0.8rem', padding: '4px 8px' }}
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  <AssignmentBox
                    label={assignmentLabel}
                    selectedIds={editAssignment}
                    users={users}
                    isEditing={true}
                    onAdd={(id) => setEditAssignment(prev => [...prev, id])}
                    onRemove={(id) => setEditAssignment(prev => prev.filter(a => a !== id))}
                    addPlaceholder={addAssignmentPlaceholder}
                    emptyPlaceholder={emptyAssignmentPlaceholder}
                  />

                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button
                      className="btn-primary"
                      style={{ fontSize: '0.78rem', padding: '5px 16px', background: 'var(--color-accent)' }}
                      onClick={() => handleSaveEdit(item)}
                    >
                      ✓ Save
                    </button>
                    <button
                      className="btn-secondary"
                      style={{ fontSize: '0.78rem', padding: '5px 12px' }}
                      onClick={handleCancelEdit}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              );
            }

            return (
              <div key={item.id} className="room-card" style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '6px', minHeight: 'auto' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <h3 style={{ fontSize: '0.95rem', color: 'var(--color-text-main)', fontWeight: '700', margin: 0 }}>
                      {item.name}
                    </h3>
                    {metaFields.length > 0 && (
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '3px', flexWrap: 'wrap' }}>
                        {metaFields.map(f => {
                          const val = item[f.key];
                          if (!val) return null;
                          return f.badge ? (
                            <span
                              key={f.key}
                              style={{
                                fontSize: '0.68rem', fontWeight: '700',
                                color: 'var(--color-primary)',
                                background: 'rgba(var(--color-primary-rgb), 0.08)',
                                border: '1px solid rgba(var(--color-primary-rgb), 0.15)',
                                padding: '1px 7px', borderRadius: '0px'
                              }}
                            >
                              {f.badgePrefix || ''}{val}
                            </span>
                          ) : (
                            <span key={f.key} style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)' }}>{val}</span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <button
                    className="btn-inline-edit"
                    style={{ fontSize: '0.72rem', padding: '2px 8px', marginLeft: '6px', flexShrink: 0 }}
                    onClick={() => handleStartEdit(item)}
                  >
                    [Edit]
                  </button>
                </div>

                <AssignmentBox
                  label={assignmentLabel}
                  selectedIds={assignmentArr}
                  users={users}
                  isEditing={false}
                  emptyPlaceholder={emptyAssignmentPlaceholder}
                />
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
